/** Display formatters scoped to PT-BR locale. */

const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const ORIG_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

const MONTH_ABBR_PT = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const

const WEEKDAY_LONG_PT = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
] as const

const WEEKDAY_SHORT_PT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const

export function formatBRL(value: number | undefined): string {
  return value === undefined ? '—' : BRL_FORMATTER.format(value)
}

export function formatOrig(value: number | undefined): string {
  return value === undefined ? '—' : ORIG_FORMATTER.format(value)
}

/** Formats `2025-04-15` as `15·ABR·25`. */
export function formatStylizedDate(iso: string): string {
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  const [year, month, day] = parts
  if (!year || !month || !day) return iso
  const monthIdx = Number.parseInt(month, 10) - 1
  const abbr = MONTH_ABBR_PT[monthIdx]
  if (!abbr) return iso
  return `${day}·${abbr.toUpperCase()}·${year.slice(2)}`
}

/** Returns the long Portuguese weekday for an ISO YYYY-MM-DD (e.g. 'quarta-feira'). */
export function weekdayLong(iso: string): string {
  const idx = isoWeekdayIndex(iso)
  if (idx === null) return ''
  return WEEKDAY_LONG_PT[idx] as string
}

/** Returns the short Portuguese weekday for an ISO YYYY-MM-DD (e.g. 'qua'). */
export function weekdayShort(iso: string): string {
  const idx = isoWeekdayIndex(iso)
  if (idx === null) return ''
  return WEEKDAY_SHORT_PT[idx] as string
}

function isoWeekdayIndex(iso: string): number | null {
  const parts = iso.split('-')
  if (parts.length !== 3) return null
  const [year, month, day] = parts
  if (!year || !month || !day) return null
  const y = Number.parseInt(year, 10)
  const m = Number.parseInt(month, 10)
  const d = Number.parseInt(day, 10)
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  const date = new Date(Date.UTC(y, m - 1, d))
  if (Number.isNaN(date.getTime())) return null
  // Guard against JS Date roll-over (e.g. month 12 day 31 OK, but day 32 in any month wraps)
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) {
    return null
  }
  return date.getUTCDay()
}

/** Returns a coarse "há X" string from a calculation timestamp. */
export function formatRelativeAgo(isoTimestamp: string, now: Date = new Date()): string {
  const then = new Date(isoTimestamp).getTime()
  const ms = now.getTime() - then
  if (!Number.isFinite(ms) || ms < 0) return '—'
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'agora'
  if (minutes < 60) return `${minutes}min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mes`
  const years = Math.floor(days / 365)
  return `${years}a`
}
